import { saveAs } from 'file-saver';
import html2canvas, { Options } from 'html2canvas';
import JSZip from 'jszip';
import React, { ReactNode } from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'theme-ui';

import rootStore from 'app-redux';
import theme from 'theme';
import { delayPromise } from 'utils/promiseUtils';

import { DefaultExportToPngWidth } from './constants';

export const ExportPrintAspectRatio = 1 / 1.294;
export const ExportPresentationAspectRatio = 16 / 9;

export type ToZipScreenshot = {
  fileName: string;
  canvas: HTMLCanvasElement;
};

export type CloneCanvasOptions = {
  bgColor?: string;
  margin?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
};

export const zipAndSaveScreenshots = async (zipFiles: ToZipScreenshot[], zipName: string) => {
  const zip = new JSZip();

  zipFiles.forEach(({ fileName, canvas }) => {
    zip.file(fileName, canvas.toDataURL().split(';base64')[1], { base64: true });
  });

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, zipName);
};

export function computeWidthScaleRatioWithMargins(
  element: HTMLElement,
  targetWidth: number,
  sourceMx: number
): number {
  return targetWidth / (element.getBoundingClientRect().width + sourceMx);
}

export function runHtml2Canvas(
  element: ReactNode,
  options: Partial<Options> = {},
  renderDelay: number = 1,
  exportWidth: number = DefaultExportToPngWidth
): Promise<HTMLCanvasElement> {
  const sect = document.createElement('div');
  // Inline-block is required or the dimensions defined in the component will be off
  sect.setAttribute('style', 'display: inline-block');

  render(
    <ThemeProvider theme={theme}>
      <Provider store={rootStore}>{element}</Provider>
    </ThemeProvider>,
    sect
  );

  // Append it outside of the viewport. Otherwise it won't render
  document.body.classList.add('no-scroll');
  document.body.appendChild(sect);
  const scale = computeWidthScaleRatioWithMargins(sect, exportWidth, 0);

  const useOptions: Partial<Options> = {
    ...{ useCORS: true, allowTaint: true, scale: scale },
    ...options,
    ...{
      onclone: (doc: Document, el: HTMLElement) => {
        // We always need to remove the section from the document. So do that on the clone and then run whatever onclone was passed
        document.body.removeChild(sect);
        if (options?.onclone) options.onclone(doc, el);

        // Waiting a ms so the scrollbar doesn't flash due to the added section not being fully removed yet
        setTimeout(() => {
          document.body.classList.remove('no-scroll');
        }, 1);
      },
    },
  };

  // NOTE: we need to let any pending UI updates finish (hence 1ms) before the client is deadlocked by html2canvas for a few seconds
  return delayPromise(null, renderDelay).then(() => {
    return html2canvas(sect, useOptions);
  });
}

export function saveCanvasToFile(
  canvas: HTMLCanvasElement,
  filename: string
): Promise<HTMLCanvasElement> {
  saveAs(canvas.toDataURL('image/png', 1.0), filename);
  return Promise.resolve(canvas);
}
